import { makeStyles, tokens } from '@fluentui/react-components';
import {
  CheckmarkCircleFilled,
  DismissCircleFilled,
  ClockFilled,
  ArrowSyncFilled,
  WarningFilled,
  SubtractCircleFilled,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  success: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  error: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  warning: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  info: {
    backgroundColor: tokens.colorPaletteBlueBorderActive,
    color: tokens.colorPaletteBlueForeground2,
  },
  pending: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
  },
  inProgress: {
    backgroundColor: tokens.colorPaletteLightTealBackground2,
    color: tokens.colorPaletteLightTealForeground2,
  },
  icon: {
    fontSize: '14px',
  },
});

type StatusType =
  | 'success' | 'error' | 'warning' | 'info' | 'pending' | 'inProgress'
  | 'Active' | 'Inactive' | 'Trial' | 'Suspended' | 'Pending'
  | 'Success' | 'Failed' | 'InProgress' | 'RolledBack' | 'Cancelled'
  | 'New' | 'Upgrade' | 'Rollback';

interface StatusBadgeProps {
  status: StatusType;
  showIcon?: boolean;
  size?: 'small' | 'medium';
}

const statusConfig: Record<string, {
  style: 'success' | 'error' | 'warning' | 'info' | 'pending' | 'inProgress';
  icon: React.ElementType;
  label: string;
}> = {
  // Generic statuses
  success: { style: 'success', icon: CheckmarkCircleFilled, label: 'Success' },
  error: { style: 'error', icon: DismissCircleFilled, label: 'Error' },
  warning: { style: 'warning', icon: WarningFilled, label: 'Warning' },
  info: { style: 'info', icon: ClockFilled, label: 'Info' },
  pending: { style: 'pending', icon: ClockFilled, label: 'Pending' },
  inProgress: { style: 'inProgress', icon: ArrowSyncFilled, label: 'In Progress' },

  // Tenant statuses
  Active: { style: 'success', icon: CheckmarkCircleFilled, label: 'Active' },
  Inactive: { style: 'pending', icon: SubtractCircleFilled, label: 'Inactive' },
  Trial: { style: 'warning', icon: ClockFilled, label: 'Trial' },
  Suspended: { style: 'error', icon: DismissCircleFilled, label: 'Suspended' },
  Pending: { style: 'pending', icon: ClockFilled, label: 'Pending' },

  // Deployment statuses
  Success: { style: 'success', icon: CheckmarkCircleFilled, label: 'Success' },
  Failed: { style: 'error', icon: DismissCircleFilled, label: 'Failed' },
  InProgress: { style: 'inProgress', icon: ArrowSyncFilled, label: 'In Progress' },
  RolledBack: { style: 'warning', icon: WarningFilled, label: 'Rolled Back' },
  Cancelled: { style: 'pending', icon: SubtractCircleFilled, label: 'Cancelled' },

  // Deployment types
  New: { style: 'info', icon: CheckmarkCircleFilled, label: 'New' },
  Upgrade: { style: 'inProgress', icon: ArrowSyncFilled, label: 'Upgrade' },
  Rollback: { style: 'warning', icon: WarningFilled, label: 'Rollback' },
};

export function StatusBadge({ status, showIcon = true, size: _size = 'medium' }: StatusBadgeProps) {
  const styles = useStyles();
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`${styles.badge} ${styles[config.style]}`}>
      {showIcon && <Icon className={styles.icon} />}
      <span>{config.label}</span>
    </span>
  );
}
